import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectCashierConfiguration } from '@cashier/cashier-data-access';
import { FastSumsTypes, RewardTypes } from '@cashier/cashier-model';
import { getCurrencySymbol } from '@cashier/cashier-shared';

import { BonusConfiguration } from './useBonusConfiguration';

interface UseBonusBadge {
  getLabel: (
    amount: string,
    bonusConfiguration: BonusConfiguration,
  ) => string | null;
}

export const useBonusBadge = (): UseBonusBadge => {
  const { currency } = useSelector(selectCashierConfiguration);

  const { t } = useTranslation();

  const measurementLabelMap: { [key: string]: string } = {
    currency: getCurrencySymbol(currency) || '',
    coins: t('coins'),
    fs: t('FS'),
  };

  const getFractionalReward = (amount: string, fraction: number): number => {
    return Number.parseFloat((Number(amount) * fraction).toFixed(2));
  };

  const getPercentageLabel = (fraction: number): string => {
    const percentage = fraction * 100;
    const formatted =
      percentage % 1 === 0
        ? percentage.toString()
        : percentage.toFixed(1).replace(/\.0$/, '');
    return `+${formatted}%`;
  };

  const getLabel = (
    amount: string,
    bonusConfiguration: BonusConfiguration,
  ): string | null => {
    const { interval, fastSumsType, effectiveFraction } = bonusConfiguration;

    if (fastSumsType === FastSumsTypes.Percent) {
      const fractionToUse = effectiveFraction ?? interval.attributes.fraction;

      return fractionToUse ? getPercentageLabel(fractionToUse) : null;
    }

    if (interval.rewardType === RewardTypes.Fixed) {
      return String(
        `+${interval.attributes.volume || interval.reward.from} ${measurementLabelMap[interval.attributes.measurement]}`,
      );
    }

    if (interval.rewardType === RewardTypes.Fractional) {
      const badgeAmount = getFractionalReward(
        amount,
        interval.attributes.fraction || 0,
      );

      return String(
        `+${badgeAmount} ${measurementLabelMap[interval.attributes.measurement]}`,
      );
    }

    return null;
  };

  return {
    getLabel,
  };
};
